import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TablePagination,
Menu,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Avatar
} from '@mui/material';
import { 
Delete, 
People, 
PictureAsPdf,
TableChart, 
GetApp,
Person,
Email,
Phone,
CalendarToday,
Work,
Business,
AttachMoney
} from '@mui/icons-material';

import {
Close as CloseIcon,
Add as AddIcon,
Search as SearchIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import { 
APIBrancheNamesFilter, 
APISalaryList, 
APISalaryStore, 
APISalaryUpdate, 
APISalaryDelete,
APIEmployeeNames // Employee names API
} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
formatCreatedAtDate, 
formatNumberWithComma, 
formatNumberWithComma2, 
getCategoriesNameById, 
getStatusById, 
getUserDetails, 
recordsPerPage, 
statusData 
} from '../../../utils/helpers';
import { 
exportSalariesToCSV, 
exportSalariesToExcel, 
exportSalariesToPDFEnhanced 
} from '../../../services/SalaryService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { salaryTypeOptions } from '../../../utils/dataInfo';

// Helper function to get initials from employee name
const getInitials = (name) => {
if (!name) return '?';
return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Helper function to get avatar color based on employee name
const stringToColor = (string) => {
let hash = 0;
for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
}
let color = '#';
for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
}
return color;
};

const Salary = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [updateId, setUpdateId] = useState('');
const [postStatus, setPostStatus] = useState('ADD');
const [exportAnchorEl, setExportAnchorEl] = useState(null);

const [pageState, setPageState] = useState({
    pageData: [],
});
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
const [selectedRecord, setSelectedRecord] = useState(null);
const [searchTerm, setSearchTerm] = useState('');
const [formData, setFormData] = useState({
    employee_id: '',
    total_salary: '',
    note: '',
    salary_type: '',
    status: '1',
    branch_id: ''
});
const [filters, setFilters] = useState({
    employee_name: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    salary_type: ''
});
const [errors, setErrors] = useState({
    employee_id: false,
    total_salary: false,
    salary_type: false,
    status: false
});

// State for dialogs
const [filterOptions, setFilterOptions] = useState(false);
const [openAdd, setOpenAdd] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [branchNamesFilterData, setBranchNamesFilterData] = useState([]);
const [selectedBranch, setSelectedBranch] = useState('');
const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);

// API data for selections
const [employeeNamesData, setEmployeeNamesData] = useState([]);

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
    fetchBranchNames();
    fetchEmployeeNames();
}, []);

const fetchBranchNames = async () => {
    try {
    const response = await axios.get(APIBrancheNamesFilter);
    if (response.data.status === 'success') {
        setBranchNamesFilterData(response.data.data || []);
    }
    } catch (error) {
    console.error("Error fetching branch names:", error);
    showToast("Error", "Failed to load branch names", "error");
    }
};

const fetchEmployeeNames = async () => {
    try {
    const response = await axios.get(APIEmployeeNames);
    if (response.data.status === 'success') {
        setEmployeeNamesData(response.data.data || []);
    }
    } catch (error) {
    console.error("Error fetching employee names:", error);
    }
};

const fetchData = async (branchId = null) => {
    setLoading(true);
    setError(null);

    try {
    let branchIdToUse = branchId;
    if (!hasPermission(PERMISSIONS.BRANCHES)) {
        branchIdToUse = getUserDetails("branchId");
    }

    const params = {};
    if (branchIdToUse) {
        params.branch_id = branchIdToUse;
    }

    const response = await axios.get(APISalaryList, { params });
    const salaries = Array.isArray(response.data.data) ? response.data.data.reverse() : [];

    setPageState(prev => ({
        ...prev,
        pageData: salaries,
    }));

    showToast("Success", TEXT_DATA_LOADED, "success");
    } catch (error) {
    let errorMessage = "Failed to load data";

    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }

    setError(errorMessage);
    console.log(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setBranchSelectionOpen(false);
    fetchData(branchId);
};

// Filter salaries based on search term and filters
const filteredData = pageState.pageData.filter(salary => {
    const employeeName = getCategoriesNameById(salary.employee_id, employeeNamesData) || '';
    
    const matchesSearch = searchTerm === '' ||
    employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    salary.note?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    salary.total_salary.toString().includes(searchTerm);

    const matchesName = !filters.employee_name || 
    employeeName.toLowerCase().includes(filters.employee_name.toLowerCase());

    const matchesStatus = !filters.status || 
    salary.status.toString() === filters.status;

    const matchesSalaryType = !filters.salary_type || 
    salary.salary_type === filters.salary_type;

    let matchesDate = true;
    if (filters.dateFrom) {
    const salaryDate = new Date(salary.created_at).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
    matchesDate = matchesDate && salaryDate >= filterFromDate;
    }

    if (filters.dateTo) {
    const salaryDate = new Date(salary.created_at).toISOString().split('T')[0];
    const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
    matchesDate = matchesDate && salaryDate <= filterToDate;
    }

    return matchesSearch && matchesName && matchesStatus && matchesSalaryType && matchesDate;
});

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handlefilterOptions = () => setFilterOptions(true);
const handleCloseOptions = () => setFilterOptions(false);

const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    employee_id: '',
    total_salary: '',
    note: '',
    salary_type: '',
    status: '1',
    branch_id: selectedBranch || ''
    });
    setOpenAdd(true);
};

const handleCloseAdd = () => {
    setOpenAdd(false);
};

const handleOpenView = (record) => {
    setSelectedRecord(record);
    setOpenView(true);
};

const handleCloseView = () => setOpenView(false);

const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("UPDATE");
    setFormData({
    employee_id: record.employee_id,
    total_salary: record.total_salary,
    note: record.note || '',
    salary_type: record.salary_type || '',
    status: record.status.toString(),
    branch_id: record.branch_id
    });
    setOpenAdd(true);
};

const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleCloseDelete = () => {
    setOpenDelete(false);
};

// Form handlers
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const applyTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
    handleCloseOptions();
};

const clearFilters = () => {
    setFilters({
    employee_name: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    salary_type: ''
    });
    setSearchTerm('');
    handleCloseOptions();
};

const validateForm = () => {
    const errors = [];
    if (!formData.employee_id) errors.push('Employee is required');
    if (!formData.total_salary) errors.push('Total Salary is required');
    if (!formData.salary_type) errors.push('Salary Type is required');
    if (!formData.status) errors.push('Status is required');

    // Validate total_salary is a number
    if (formData.total_salary && isNaN(parseFloat(formData.total_salary))) {
    errors.push('Total Salary must be a valid number');
    }

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleDialogClose = () => {
    setOpenAdd(false);
    setOpenView(false);
    setOpenDelete(false);
    setFilterOptions(false);
};

const handleFormDataReset = () => {
    setFormData({
    employee_id: '',
    total_salary: '',
    note: '',
    salary_type: '',
    status: '1',
    branch_id: selectedBranch || ''
    });
};

// Export handlers
const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
    setExportAnchorEl(null);
};

const handleExportExcel = async () => {
    try {
    handleExportClose();
    await exportSalariesToExcel(filteredData,employeeNamesData,branchNamesFilterData,);
    showToast("Success", "Excel file downloaded successfully", "success");
    } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportPDF = async () => {
    try {
    handleExportClose();
    await exportSalariesToPDFEnhanced(filteredData,employeeNamesData,branchNamesFilterData, {
        employee_name: filters.employee_name,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        status: filters.status,
        searchTerm: searchTerm
    });
    showToast("Success", "PDF file downloaded successfully", "success");
    } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportCSV = async () => {
    try {
    handleExportClose();
    await exportSalariesToCSV(filteredData,employeeNamesData,branchNamesFilterData);
    showToast("Success", "CSV file downloaded successfully", "success");
    } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
    }
};

const getSelectedBranchName = () => {
    if (!selectedBranch) return "Select Branch";
    const branch = branchNamesFilterData.find(b => b.id === selectedBranch);
    return branch ? `${branch.name} (${branch.code})` : "Select Branch";
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('user_id', getUserDetails("Id"));

    // Set branch_id based on permissions
    if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
    formDataToSend.append('branch_id', selectedBranch);
    } else {
    formDataToSend.append('branch_id', getUserDetails("branchId"));
    }
    
    // Append all form data one by one
    formDataToSend.append('employee_id', formData.employee_id || '');
    formDataToSend.append('total_salary', formData.total_salary || '');
    formDataToSend.append('note', formData.note || '');
    formDataToSend.append('salary_type', formData.salary_type || '');
    formDataToSend.append('status', formData.status || '1');
    
    // For UPDATE and DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
    formDataToSend.append('updateId', updateId);
    }

    try {
    let apiUrl, method;
    if (postStatus === "ADD") {
        apiUrl = APISalaryStore;
        method = 'post';
    } else if (postStatus === "UPDATE") {
        apiUrl = APISalaryUpdate;
        method = 'post';
    } else if (postStatus === "DELETE") {
        apiUrl = APISalaryDelete;
        method = 'post';
    }

    const response = await axios({
        method,
        url: apiUrl,
        data: formDataToSend,
        headers: {
        'Content-Type': 'multipart/form-data'
        }
    });

    if (response.data.status === "success") {
        showToast("Success", response.data.message, "success");
        handleFormDataReset();
        handleDialogClose();
        fetchData();
    } else {
        showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
    }
    } catch (error) {
    if (error.response) {
        const { status, data } = error.response;
        if (status === 422) {
        const errors = data.errors || {};
        let errorMessages = [];
        for (const field in errors) {
            errorMessages.push(`${field}: ${errors[field].join(', ')}`);
        }
        showToast(
            "Validation Error",
            errorMessages.join('\n') || "Please correct the form errors",
            "error"
        );
        } else if (status === 409) {
        showToast("Error", data.message || "This record already exists", "error");
        } else if (status >= 500) {
        showToast("Server Error", data.message || "Internal server error", "error");
        } else {
        showToast("Error", data.message || "Request failed", "error");
        }
    } else if (error.request) {
        showToast("Network Error", "No response from server. Please check your connection.", "error");
    } else {
        showToast("Error", error.message || "An unexpected error occurred", "error");
    }
    console.error("API Error:", error);
    }
};

if (loading) return <div>Loading...</div>;
if (error) return <div>Error: {error}</div>;

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
    }}>
        Salaries Management ( {formatNumberWithComma2(pageState.pageData.length)} Total )
    </Typography>
    
    {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
        <Box sx={{ mt: 1, mb: 2 }}>
        <Chip
            label={`Selected Branch: ${getSelectedBranchName()}`}
            color="primary"
            variant="filled"
            onDelete={() => {
            setSelectedBranch('');
            fetchData();
            }}
            deleteIcon={<CloseIcon />}
        />
        </Box>
    )}

    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            order: { xs: 2, sm: 1 }
        }}>
            {hasPermission(PERMISSIONS.ADD) && (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Add New Salary
            </Button>
            )}

            {hasPermission(PERMISSIONS.BRANCHES) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<FilterIcon />}
                onClick={() => setBranchSelectionOpen(true)}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Select Branch
            </Button>
            )}
            
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={handlefilterOptions}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            View Options
            </Button>
            
            {hasPermission(PERMISSIONS.EXPORT) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportClick}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Export Data
            </Button>
            )}
        </Box>

        <Paper sx={{
            width: { xs: '100%', sm: '300px' },
            p: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            order: { xs: 1, sm: 2 },
            borderRadius: '8px',
            background: theme.palette.primary.light
        }}>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search salaries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                sx: {
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
                color: 'white',
                '& input': {
                    color: 'white',
                    '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1
                    }
                }
                }
            }}
            size="small"
            />
        </Paper>
        </Box>

        {(filters.employee_name || filters.dateFrom || filters.dateTo || filters.status || filters.salary_type) && (
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
            Active filters:
            </Typography>
            {filters.employee_name && (
            <Chip
                label={`Employee: ${filters.employee_name}`}
                onDelete={() => setFilters(prev => ({ ...prev, employee_name: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.status && (
            <Chip
                label={`Status: ${getStatusById(filters.status)}`}
                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.salary_type && (
            <Chip
                label={`Salary Type: ${filters.salary_type}`}
                onDelete={() => setFilters(prev => ({ ...prev, salary_type: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateFrom && (
            <Chip 
                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateTo && (
            <Chip 
                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            <Button 
            size="small"
            onClick={clearFilters}
            startIcon={<CloseIcon />}
            sx={{ ml: 1 }}
            >
            Clear All
            </Button>
        </Box>
        )}
    </Paper>

    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
            <Table>
            <TableHead>
                <TableRow sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                }
                }}>
                <TableCell>#</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Total Salary</TableCell>
                <TableCell>Salary Type</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Status</TableCell>
                {hasPermission(PERMISSIONS.BRANCHES) && <TableCell>Branch</TableCell>}
                <TableCell>Created Date</TableCell>
                <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((salary, index) => {
                const employeeName = getCategoriesNameById(salary.employee_id, employeeNamesData);
                const initials = getInitials(employeeName);
                const avatarColor = stringToColor(employeeName);
                
                return (
                    <TableRow
                    key={salary.id}
                    hover
                    sx={{
                        '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.action.hover
                        }
                    }}
                    >
                    <TableCell>
                        <Chip
                        label={page * rowsPerPage + index + 1}
                        color="primary"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                            sx={{ 
                            width: 32, 
                            height: 32, 
                            bgcolor: avatarColor,
                            fontSize: '12px',
                            fontWeight: 'bold'
                            }}
                        >
                            {initials}
                        </Avatar>
                        <Typography variant="body2">
                            {employeeName}
                        </Typography>
                        </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'green' }}>
                        {formatNumberWithComma(salary.total_salary)}
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={salary.salary_type || 'N/A'}
                        color="secondary"
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: '200px' }}>
                        {salary.note || 'No note'}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        {salary.status === 1 ? (
                        <Chip
                            label={getStatusById(salary.status)}
                            size="small"
                            color="primary"
                        />
                        ) : (
                        <Chip
                            label={getStatusById(salary.status)}
                            size="small"
                            color="error"
                        />
                        )}
                    </TableCell>
                    {hasPermission(PERMISSIONS.BRANCHES) && (
                        <TableCell>
                        <Chip
                            label={getCategoriesNameById(salary.branch_id, branchNamesFilterData)}
                            color="warning"
                            variant="outlined"
                            size="small"
                        />
                        </TableCell>
                    )}
                    <TableCell>
                        <Typography variant="body2">
                        {formatCreatedAtDate(salary.created_at)}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenView(salary)}
                        color="primary"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.info.light
                            }
                        }}
                        >
                        <ViewIcon />
                        </IconButton>
                        {hasPermission(PERMISSIONS.EDIT) && (<>
                        <IconButton
                            edge="end"
                            onClick={() => handleOpenEdit(salary)}
                            color="secondary"
                            sx={{
                            '&:hover': {
                                backgroundColor: theme.palette.success.light
                            }
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                        </>)}
                        {hasPermission(PERMISSIONS.DELETE) && (<>
                        <IconButton
                            edge="end"
                            onClick={() => handleOpenDelete(salary)}
                            color="error"
                            sx={{
                            '&:hover': {
                                backgroundColor: theme.palette.error.light
                            }
                            }}
                        >
                            <Delete />
                        </IconButton>
                        </>)}
                    </TableCell>
                    </TableRow>
                );
                })}
            </TableBody>
            </Table>
        </TableContainer>

        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
        />

        {filteredData.length === 0 && (
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary'
            }}>
            <AttachMoney sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No salary records found</Typography>
            <Typography variant="body2">Try adjusting your search query or add a new salary record</Typography>
            </Box>
        )}
        </Paper>
    </motion.div>

    {/* Branch Selection Dialog */}
    <Dialog
        open={branchSelectionOpen}
        onClose={() => setBranchSelectionOpen(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
        <DialogContent>
        <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
            <InputLabel id="branch-select-label">Select Branch</InputLabel>
            <Select
                labelId="branch-select-label"
                value={selectedBranch}
                label="Select Branch"
                onChange={(e) => handleBranchSelect(e.target.value)}
            >
                <MenuItem value="">
                <em>Select a branch</em>
                </MenuItem>
                {branchNamesFilterData.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setBranchSelectionOpen(false)}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={() => {
            if (selectedBranch) {
                fetchData(selectedBranch);
            }
            setBranchSelectionOpen(false);
            }}
            color="primary"
            variant="contained"
        >
            Apply
        </Button>
        </DialogActions>
    </Dialog>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
        }}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>

    {/* Options Dialog */}
    <Dialog
        open={filterOptions}
        onClose={handleCloseOptions}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Show All Salaries
            </Button>

            <Button
            variant="outlined"
            color="primary"
            startIcon={<TodayIcon />}
            onClick={applyTodayFilter}
            sx={{ textTransform: 'none' }}
            >
            Show Today's Salaries
            </Button>

            <Divider>OR</Divider>

            <TextField
            fullWidth
            label="Employee Name"
            name="employee_name"
            value={filters.employee_name}
            onChange={handleFilterChange}
            />

            <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
            >
                <MenuItem value=""><em>All Status</em></MenuItem>
                {statusData.map((item, index) => (
                <MenuItem key={index} value={item.id}>
                    {item.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            <FormControl fullWidth>
            <InputLabel>Salary Type</InputLabel>
            <Select
                name="salary_type"
                value={filters.salary_type}
                onChange={handleFilterChange}
                label="Salary Type"
            >
                <MenuItem value=""><em>All Salary Types</em></MenuItem>
                {salaryTypeOptions.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                    {type.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="From Date"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="To Date"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseOptions}
            variant="contained"
        >
            Close
        </Button>
        <Button
            onClick={handleCloseOptions}
            color="primary"
            variant="contained"
        >
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* Add/Edit Dialog */}
    <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
        scroll="paper"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>
        <b>{postStatus === "ADD" ? "Add New Salary" : "Edit Salary"}</b>
        </DialogTitle>
        <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
            <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Employee *</InputLabel>
                <Select
                    name="employee_id"
                    value={formData.employee_id}
                    onChange={handleInputChange}
                    label="Employee *"
                    error={errors.employee_id}
                    required
                >
                    <MenuItem value=""><em>Select Employee</em></MenuItem>
                    {employeeNamesData.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                        {employee.employee_name}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                <TextField
                fullWidth
                label="Total Salary *"
                name="total_salary"
                type="number"
                value={formData.total_salary}
                onChange={handleInputChange}
                error={errors.total_salary}
                helperText={errors.total_salary ? "Required" : ""}
                required
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Salary Type *</InputLabel>
                <Select
                    name="salary_type"
                    value={formData.salary_type}
                    onChange={handleInputChange}
                    label="Salary Type *"
                    error={errors.salary_type}
                    required
                >
                    <MenuItem value=""><em>Select Salary Type</em></MenuItem>
                    {salaryTypeOptions.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                        {type.name}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Status *</InputLabel>
                <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Status *"
                    error={errors.status}
                    required
                >
                    {statusData.map((item, index) => (
                    <MenuItem key={index} value={item.id}>
                        {item.name}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                {hasPermission(PERMISSIONS.BRANCHES) && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Branch *</InputLabel>
                    <Select
                    name="branch_id"
                    value={formData.branch_id}
                    onChange={handleInputChange}
                    label="Branch *"
                    required
                    >
                    <MenuItem value=""><em>Select Branch</em></MenuItem>
                    {branchNamesFilterData.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                        {branch.name} ({branch.code})
                        </MenuItem>
                    ))}
                    </Select>
                </FormControl>
                )}

                <TextField
                fullWidth
                multiline
                rows={3}
                label="Note"
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                placeholder="Additional notes about the salary..."
                />
            </Grid>
            </Grid>

            <DialogActions sx={{ mt: 3, px: 0 }}>
            <Button
                color="error"
                onClick={handleCloseAdd}
                variant="contained"
            >
                Cancel
            </Button>
            <Button
                type="submit"
                color="primary"
                variant="contained"
            >
                {postStatus === "ADD" ? "Add Salary" : "Update Salary"}
            </Button>
            </DialogActions>
        </Box>
        </DialogContent>
    </Dialog>

    {/* View Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Salary Details</b></DialogTitle>
        <DialogContent>
        {selectedRecord && (() => {
            const employeeName = getCategoriesNameById(selectedRecord.employee_id, employeeNamesData);
            const initials = getInitials(employeeName);
            const avatarColor = stringToColor(employeeName);
            
            return (
            <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar
                        sx={{ 
                        width: 60, 
                        height: 60, 
                        bgcolor: avatarColor,
                        fontSize: '20px',
                        fontWeight: 'bold'
                        }}
                    >
                        {initials}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                        {employeeName}
                        </Typography>
                        <Chip
                        label={getStatusById(selectedRecord.status)}
                        color={selectedRecord.status === 1 ? "primary" : "error"}
                        size="small"
                        />
                    </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="h6" sx={{ color: 'white' }}>
                        Total Salary:
                        </Typography>
                        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {formatNumberWithComma(selectedRecord.total_salary)}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Work fontSize="small" />
                        <Typography variant="subtitle2">
                        <strong>Salary Type:</strong> {selectedRecord.salary_type || 'N/A'}
                        </Typography>
                    </Box>

                    {hasPermission(PERMISSIONS.BRANCHES) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business fontSize="small" />
                        <Typography variant="subtitle2">
                            <strong>Branch:</strong> {getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData)}
                        </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday fontSize="small" />
                        <Typography variant="subtitle2">
                        <strong>Created Date:</strong> {formatCreatedAtDate(selectedRecord.created_at)}
                        </Typography>
                    </Box>

                    {selectedRecord.note && (
                        <>
                        <Typography variant="subtitle2" gutterBottom>
                            <strong>Note:</strong>
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            p: 2, 
                            bgcolor: 'action.hover', 
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap'
                        }}>
                            {selectedRecord.note}
                        </Typography>
                        </>
                    )}
                    </Box>
                </Grid>
                </Grid>
            </Box>
            );
        })()}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
        >
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        TransitionComponent={Transition}
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Delete Salary Record</DialogTitle>
        <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete this salary record?
        </Typography>
        {selectedRecord && (() => {
            const employeeName = getCategoriesNameById(selectedRecord.employee_id, employeeNamesData);
            
            return (
            <Box sx={{ textAlign: 'center' }}>
                <Avatar
                sx={{ 
                    width: 60, 
                    height: 60, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: 'error.main'
                }}
                >
                <AttachMoney sx={{ color: 'white' }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {employeeName}
                </Typography>
                <Typography variant="body2" gutterBottom sx={{ color: 'error.main', fontWeight: 'bold' }}>
                {formatNumberWithComma(selectedRecord.total_salary)}
                </Typography>
                <Typography variant="body2" gutterBottom>
                {selectedRecord.salary_type || 'N/A'}
                </Typography>
                {hasPermission(PERMISSIONS.BRANCHES) && (
                <Typography variant="body2">
                    Branch: {getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData) || 'N/A'}
                </Typography>
                )}
            </Box>
            );
        })()}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseDelete}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
        >
            Confirm Delete
        </Button>
        </DialogActions>
    </Dialog>
    </Container>
);
};

export default Salary;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});

